import { Component, Inject, OnInit } from "@angular/core";
import {
  UntypedFormGroup,
  UntypedFormBuilder,
  Validators,
} from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { AuthService } from "src/app/core/services/auth.service";
import {
  ContactWithRelations,
  ContactTypeWithRelations,
  ContactRoleWithRelations,
  Contact,
} from "src/app/api/models";
import { TinyService } from "src/app/shared/services/tiny.service";

export interface ContactCreateUpdateDialogData {
  context: "editor" | "inventory" | "item";
  contactTypes: ContactTypeWithRelations[];
  contactRoles: ContactRoleWithRelations[];
  contact?: ContactWithRelations;
}

export interface ContactCreateUpdateDialogResult {
  action: "create" | "update" | "delete";
  contact?: Contact;
}

@Component({
  selector: "app-contact-create-update-dialog",
  templateUrl: "./contact-create-update-dialog.component.html",
  styleUrls: ["./contact-create-update-dialog.component.scss"],
  standalone: false,
})
export class ContactCreateUpdateDialogComponent implements OnInit {
  form: UntypedFormGroup;
  mode: "create" | "update" = "create";
  context: "editor" | "inventory" | "item";

  contact: ContactWithRelations;
  contactTypes: ContactTypeWithRelations[] = [];
  contactRoles: ContactRoleWithRelations[] = [];
  filteredContactRoles: ContactRoleWithRelations[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public defaults: ContactCreateUpdateDialogData,
    private dialogRef: MatDialogRef<ContactCreateUpdateDialogComponent>,
    private fb: UntypedFormBuilder,
    private authService: AuthService,
    public tinyService: TinyService,
  ) {}

  async ngOnInit(): Promise<void> {
    await this.init();
  }

  /** Default submit function */
  save() {
    if (this.mode === "create") {
      this.create();
    } else if (this.mode === "update") {
      this.update();
    }
  }

  private create() {
    const roleIds: number[] = this.form.get("roles").value;
    const roles = this.contactRoles.filter((a) => roleIds.includes(a.id));
    const newContact: ContactWithRelations = {
      ...this.form.value,
    };
    newContact.roles = roles;
    delete newContact.exhibitions;
    delete newContact.files;
    this.dialogRef.close(newContact);
  }
  private update() {
    const roleIds: number[] = this.form.get("roles").value;
    const roles = this.contactRoles.filter((a) => roleIds.includes(a.id));
    const updatedContact: ContactWithRelations = {
      ...this.contact,
      ...this.form.value,
    };
    updatedContact.roles = roles;
    delete updatedContact["contactType"];
    delete updatedContact.exhibitions;
    delete updatedContact.files;

    this.dialogRef.close(updatedContact);
  }

  /* ---------------------------------------------------------
  DIALOG CONTENT METHODS
  --------------------------------------------------------- */
  isCreateMode() {
    return this.mode === "create";
  }

  isUpdateMode() {
    return this.mode === "update";
  }

  isEditorContext() {
    return this.context === "editor";
  }

  isInventoryContext() {
    return this.context === "inventory";
  }

  /**
   * Determines whether the current user has the ability to edit the contact form, based on their role,
   * the current context (editor, inventory, etc.), and whether the form is in create or update mode.
   *
   * Conditions for allowing edits:
   * - User is in editor context.
   * - The contact is a local contact (has an `inventoryId`) and the form is in update mode within the inventory context.
   * - The user has an editor or admin role, is in update mode, and is within the inventory context.
   * - The form is in create mode.
   *
   * @returns {boolean} True if the user can edit the form, otherwise false.
   */
  canEdit() {
    // Check if the contact is local by checking if it has an `inventoryId`
    const isLocalContact = this.defaults.contact?.inventoryId;

    // Check if the user has the right roles (editor or admin) to edit global contacts in the inventory context
    const canEditGlobalInInventory =
      this.authService.checkUserAccessRightByRole("editor") ||
      this.authService.checkUserAccessRightByRole("admin");

    // Return true if any of the following conditions are met:
    return (
      this.isEditorContext() || // User is in the editor context
      (this.isUpdateMode() && isLocalContact && this.isInventoryContext()) || // The contact is local, in update mode, and in the inventory context
      (this.isUpdateMode() &&
        canEditGlobalInInventory &&
        this.isInventoryContext()) || // The user has editor or admin role, in update mode, and in the inventory context
      this.isCreateMode() // The form is in create mode
    );
  }

  async init(): Promise<void> {
    this.context = this.defaults.context;
    this.contactTypes = this.defaults.contactTypes;
    this.contactRoles = this.defaults.contactRoles;
    this.filteredContactRoles = this.defaults.contactRoles;
    this.contact = this.defaults.contact ?? ({} as ContactWithRelations);
    if (this.defaults.contact) {
      this.mode = "update";
      this.prepareForm();
    } else {
      this.contact = {} as ContactWithRelations;
      this.prepareForm();
    }
    if (!this.canEdit()) {
      this.form.disable({ emitEvent: false });
    }
  }

  private prepareForm() {
    const roles = [];
    this.contact.roles?.map((role) => roles.push(role.id));
    this.form = this.fb.group({
      title: [this.contact.title, { validators: Validators.required }],
      firstName: [this.contact.firstName],
      lastName: [this.contact.lastName],
      companyName: [this.contact.companyName],
      contactTypeId: [
        this.contact.contactTypeId,
        { validators: Validators.required },
      ],
      notesInternal: [this.contact.notesInternal],
      roles: [roles],
      inventoryId: [this.contact.inventoryId],
      curated: [this.contact.curated ?? false],
      isPublic: [this.contact.isPublic ?? false],
    });
  }

  //TODO: When more roles from not covering filter are selected, only last one filtered is saved
  filterContactRoles(event: ContactRoleWithRelations[]) {
    this.filteredContactRoles = event;
  }
}
